import React, { ReactElement, useMemo } from 'react';
import Box from '@mui/material/Box';
import { CircularProgress, Typography } from '@mui/material';
import { StarIcon } from 'components/Icons/StarIcon';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import InfoIcon from '@mui/icons-material/Info';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

type Props = {
  title: string | ReactElement;
  subtitle?: string | ReactElement;
  type: 'success' | 'info' | 'error' | 'warning';
  isLoading?: boolean;
  maxWidth?: number;
  width?: number | string;
};

export const AlertCard: React.FC<Props> = ({
  title,
  subtitle,
  type,
  isLoading,
  maxWidth,
  width
}) => {
  const { backgroundColor, icon, color } = useMemo(() => {
    switch (type) {
      case 'info':
        return {
          backgroundColor: 'info.main',
          icon: isLoading ? (
            <CircularProgress sx={{ color: 'info.contrastText' }} size={24} />
          ) : (
            <InfoOutlined sx={{ color: 'info.contrastText' }} />
          ),
          color: 'info.contrastText'
        };
      case 'error':
        return {
          backgroundColor: 'error.light',
          icon: <CancelIcon />,
          color: 'error.main'
        };
      case 'success':
        return {
          backgroundColor: 'success.main',
          icon: <CheckCircleIcon />,
          color: 'success.contrastText'
        };
      case 'warning':
        return {
          backgroundColor: 'warning.main',
          icon: <InfoIcon sx={{ color: 'warning.light' }} />,
          color: 'warning.contrastText'
        };
      default:
        return {
          backgroundColor: 'customGrey.medium',
          icon: <StarIcon />,
          color: 'customGrey.dark'
        };
    }
  }, [type, isLoading]);

  return (
    <Box
      sx={{
        backgroundColor: backgroundColor,
        p: 2,
        maxWidth: maxWidth,
        width
      }}
    >
      <Box sx={{ display: 'flex', color }}>
        {icon}
        <Box sx={{ ml: 2, width: '100%' }}>
          <Typography sx={{ color, fontWeight: '600' }}>{title}</Typography>
          {typeof subtitle === 'string' ? (
            <Typography sx={{ color }}>{subtitle}</Typography>
          ) : (
            subtitle
          )}
        </Box>
      </Box>
    </Box>
  );
};
